import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, CardBody, CardHeader, Col, Collapse, Row, Spinner } from 'reactstrap';
import { useExecutionInfo } from '../hooks/useExecutionInfo';
import { UtterancesResponse, useUtterancesJson } from '../hooks/useUtterancesJson';
import { useStoreActions } from '../stores/store';
import { secondsToTimeString } from '../utils/secondsToTimeString';
import { countWords } from '../utils/countWords';

const ExecutionUtterances: React.FC<{ id: string }> = ({ id }) => {
  const { data, isLoading, error } = useExecutionInfo(id)
  const { data: utterances } = useUtterancesJson(data?.utternacesJSONUrl)

  const [collapsed, setCollapsed] = useState(true)
  const [searchString, setSearchString] = useState('')
  const [finalSearchTerm, setFinalSearchTerm] = useState('')
  const [searched, setSearched] = useState(false)
  const [filteredData, setFilteredData] = useState<UtterancesResponse>([])
  const seekTo = useStoreActions(actions => actions.seekTo)
  const [currSpeakerLabel, setCurrSpeakerLabel] = useState<string | undefined>(undefined)

  const totalSpeakers = useMemo(() => {
    if (!utterances) return 0
    return new Set(utterances.map(u => u.speaker)).size
  }, [utterances])

  const totalWords = useMemo(() => {
    if (!utterances) return 0
    return utterances.reduce((acc, curr) => acc + countWords(curr.text), 0)
  }, [utterances])

  const percentageWordsBySpeaker = useMemo(() => {
    if (!utterances) return []
    const speakers = Array.from(new Set(utterances.map(u => u.speaker)))
    const totalWordsBySpeaker = speakers.map(s => {
      const words = utterances.filter(u => u.speaker === s).reduce((acc, curr) => acc + countWords(curr.text), 0)
      return { speaker: s, words }
    })
    return totalWordsBySpeaker
      .map(s => ({ ...s, percentage: (s.words / totalWords) * 100 }))
      .sort((a, b) => b.percentage - a.percentage)
  }, [utterances, totalWords])

  useEffect(() => {
    if (!finalSearchTerm || !utterances) return
    if (collapsed) {
      setCollapsed(false)
    }
    setSearchString(finalSearchTerm)
    setSearched(true)

    setFilteredData(utterances.filter(u => {
      const regex = new RegExp(`.*${finalSearchTerm}.*`, 'ig')
      return regex.test(u.text)
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalSearchTerm])

  const goToTopAndSeek = (ms: number) => {
    window.scrollTo(0, 0)
    seekTo(ms)
  }

  const clearSearch = () => {
    setSearched(false)
    setFinalSearchTerm('')
    // setPrevSearchedString('')
    // setFilteredData(initialCueItems)
  }

  const handleSearch = () => {
    if (!searchString) {
      clearSearch()
      return
    }
    if (collapsed) {
      setCollapsed(false)
    }
    setFinalSearchTerm(searchString)
  }

  const handleClickLabel = (label?: string) => {
    setCurrSpeakerLabel(label)
    setSearched(true)
    if (collapsed) {
      setCollapsed(false)
    }
    if (!label) {
      setFilteredData(utterances || [])
      return
    }
    setFilteredData(utterances?.filter(u => u.speaker === label) || [])
  }

  let content: JSX.Element | null = null

  if (isLoading) {
    content = <Spinner />
  } else if (!data || error) {
    content = (
      <Alert color="danger">
        Something went wrong!
      </Alert>
    )
  } else {
    content = (
      <>
        {
          searched ?
            <>
              {
                filteredData.length === 0 &&
                <Alert color="warning">
                  No results found!
                </Alert>
              }
              {
                filteredData.map((f, i) => (
                  <div
                    key={i}
                  >
                    <p
                      style={{
                        marginBottom: 0,
                        paddingBottom: 0
                      }}
                    >
                      <b>{f.speaker}: </b>
                      {
                        finalSearchTerm ?
                          <span
                            dangerouslySetInnerHTML={{
                              __html: f.text
                                .replace(
                                  new RegExp(finalSearchTerm, 'ig'),
                                  `<b style="color: orange; padding: 0; margin: 0;">
                                  ${finalSearchTerm}
                                </b>`
                                )
                            }}
                          />
                          :
                          <span>{f.text}</span>
                      }
                    </p>
                    <small>Talked at {secondsToTimeString(f.start / 1000)}</small>
                    <br />
                    <Button
                      size="sm"
                      color="primary"
                      onClick={() => goToTopAndSeek(f.start)}
                    >
                      Seek here
                    </Button>
                    <hr />
                  </div>
                ))
              }
            </>
            :
            <>
              {
                utterances?.map((f, i) => (
                  <div
                    key={i}
                  >
                    <p
                      style={{
                        marginBottom: 0,
                        paddingBottom: 0
                      }}
                    >
                      <b>{f.speaker}: </b>
                      <span>{f.text}</span>
                    </p>
                    <small>Talked at {secondsToTimeString(f.start / 1000)}</small>
                    <br />
                    <Button
                      size="sm"
                      color="primary"
                      onClick={() => goToTopAndSeek(f.start)}
                    >
                      Seek here
                    </Button>
                    <hr />
                  </div>
                ))
              }
            </>
        }
      </>
    )
  }


  return (
    <>
      <Row>
        <Col className="order-xl-1">
          <Card className="bg-secondary shadow">
            <CardHeader className="bg-white border-0">
              <Row className="align-items-center">
                <Col xs="8" sm="3">
                  <h3 className="mb-0">
                    ({totalSpeakers}) Speakers
                  </h3>
                </Col>
                <Col xs="8" sm="5">
                  <div className="input-group-alternative input-group">
                    <div className="input-group-prepend" style={{ width: '100%' }}>
                      <span onClick={handleSearch} className="input-group-text">
                        <i className="fas fa-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search for anything"
                        id="subt_search"
                        value={searchString}
                        onChange={e => {
                          setSearchString(e.target.value)
                        }}
                        onKeyDown={e => {
                          if (e.key.toLowerCase() === 'enter') {
                            handleSearch()
                          }
                        }}
                      />
                    </div>
                  </div>
                </Col>
                <Col className="text-right" xs="4">
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => {
                      setCollapsed(!collapsed)
                    }}
                  >
                    {collapsed ? 'Expand' : 'Collapse'}
                  </Button>
                </Col>
              </Row>
              <Row className="float-right mt-2">
                <Col>
                  {
                    percentageWordsBySpeaker.map((p, i) => (
                      <Button
                        size='sm'
                        color='primary'
                        key={i}
                        onClick={() => handleClickLabel(p.speaker)}
                        disabled={currSpeakerLabel === p.speaker}
                      >
                        Speaker {p.speaker} ({Math.floor(p.percentage)}%)
                      </Button>
                    ))
                  }
                  <Button
                    size='sm'
                    color='primary'
                    onClick={() => handleClickLabel()}
                    disabled={!currSpeakerLabel}
                  >
                    Clear
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <Collapse isOpen={!collapsed}>
              <CardBody>
                {content}
              </CardBody>
            </Collapse>
          </Card>
        </Col>
      </Row>
      <br />
      {/* {summary &&
        <TextModal
          header={`${selectedSummary} Summary`}
          text={selectedSummary ? summary[selectedSummary] : ''}
          open={summaryModalOpen}
          toggle={() => setSummaryModalOpen(false)}
        />
      } */}
    </>
  );
}

export default ExecutionUtterances
